<template>
    <div class="gradient-border p-6 rounded-3xl bg-white flex flex-col gap-6 max-w-[600px] mt-[30px]">
        <h2 class="text-valence-primary-500 title-body">Feedback for {{ userDetails.first_name }}</h2>
        <div class="title-body flex flex-col gap-1 md:w-[530px] text-valence-grey-750">{{ question }}</div>
        <div class="flex justify-between items-center gap-2.5">
            <BaseButton
                theme="tertiary"
                class="flex justify-center items-center gap-3.5 whitespace-nowrap hover:rounded-3xl text-valence-primary-500 title-body w-full py-3! max-w-[200px]"
                @click="handleShowPreview()">
                Preview Invitation
                <i class="bi bi-arrows-angle-expand" aria-hidden="true"></i>
            </BaseButton>
            <div class="flex gap-2">
                <BaseButton
                    theme="primary"
                    :disabled="buttonClicked"
                    :class="[buttonClicked ? 'bg-valence-primary-500/80' : 'bg-valence-primary-500', 'disabled:bg-[#AEB1D2]']"
                    class="px-6 py-3! rounded-3xl! justify-center"
                    @click="copyLink()"
                    >{{ buttonText }}</BaseButton
                >
                <BaseButton
                    theme="tertiary"
                    :disabled="urlButtonClicked"
                    class="px-6 py-3! rounded-3xl! justify-center whitespace-nowrap text-valence-primary-500"
                    @click="copyUrl()"
                    >{{ urlButtonText }}</BaseButton
                >
            </div>
        </div>
    </div>
    <!-- Preview Modal -->
    <BaseDialog v-model:open="showPreview" title="Preview feedback request">
        <div class="flex flex-col justify-start w-full h-auto min-h-[95vh] md:w-[65vw] max-w-[1050px] bg-[#FAFAFA] border-b-2">
            <div class="flex flex-row justify-between items-center px-6 w-full border-b py-5">
                <div ref="downloadMenuContainer"></div>
                <p class="text-valence-grey-750 title-subsection">Preview</p>
                <BaseButton theme="neutral" class="border-none" @click="showPreview = false">
                    <i class="bi bi-x text-[30px] text-valence-grey-750" aria-hidden="true"></i>
                </BaseButton>
            </div>
            <!-- Preview Content -->
            <FeedbackResponseLandingPage preview class="md:p-4" :recipient_users_name="userDetails.first_name" />
        </div>
    </BaseDialog>
</template>

<script setup>
import { useFetch } from "~vue/api";
import BaseDialog from "~vue/components/BaseDialog.vue";
import { useTimedShow } from "~vue/composables/useTimedShow";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, ref } from "vue";

import BaseButton from "../components/BaseButton.vue";
import FeedbackResponseLandingPage from "./FeedbackResponseLandingPage.vue";

const props = defineProps({
    question: { type: String, required: true },
    messageId: { type: Number, required: true },
});

const { emitter } = inject("globalProperties");
const userDetails = inject("user_details");
const coachingSessionUuid = inject("coachingSessionUuid");

const { show: showCopied, isShowing: buttonClicked } = useTimedShow({ duration: 1500 });
const buttonText = computed(() => (buttonClicked.value ? "Copied!" : "Copy Invite"));

const { show: showUrlCopied, isShowing: urlButtonClicked } = useTimedShow({ duration: 1500 });
const urlButtonText = computed(() => (urlButtonClicked.value ? "Copied!" : "Copy URL"));

const showPreview = ref(false);
const handleShowPreview = () => {
    if (!showPreview.value === true) {
        logUserInteraction("feedback_landing_page_previewed", {}, coachingSessionUuid);
    }
    showPreview.value = !showPreview.value;
};

const {
    execute: createFeedbackRequestQuestion,
    data: feedbackQuestion,
    onFetchResponse: onCreateFeedbackQuestion,
} = useFetch("/api/accounts/feedback-questions", { immediate: false })
    .post(
        computed(() => ({
            question: props.question,
            chat_message: props.messageId,
        })),
    )
    .json();

const { execute: generateFeedbackLink, data: feedbackReview } = useFetch("/api/accounts/feedback-url", { immediate: false })
    .post(computed(() => ({ question_id: feedbackQuestion.value?.id })))
    .json();

onCreateFeedbackQuestion(async () => {
    logUserInteraction(
        "feedback_request_question_created",
        {
            question: props.question,
            question_id: feedbackQuestion.value?.hashid,
        },
        coachingSessionUuid,
    );
});

function showError(message) {
    emitter.emit("error_notification", message);
}

const feedbackReviewLink = computed(() => feedbackReview.value?.url);

const copyLink = async () => {
    if (!feedbackReviewLink.value) {
        try {
            await createFeedbackRequestQuestion();
            await generateFeedbackLink();
        } catch (error) {
            logUserInteraction("feedback_request_link_copy_error", { error: error.message }, coachingSessionUuid);
            showError(error.message);
            return;
        }
    }
    logUserInteraction("feedback_request_link_copied", { feedback_request_id: feedbackQuestion.value?.hashid }, coachingSessionUuid);

    if (feedbackReviewLink.value) {
        const message = `Hi, I'd love to get your anonymous perspective on: "${props.question}". It only takes a few minutes: ${feedbackReviewLink.value}`;
        navigator.clipboard.writeText(message).then(() => {
            showCopied();
        });
    } else {
        logUserInteraction(
            "feedback_request_link_copy_error",
            { error: "No feedback review link available", feedback_request_id: feedbackQuestion.value?.hashid },
            coachingSessionUuid,
        );
        showError("Error generating feedback review link. Please refresh and try again.");
    }
};

const copyUrl = async () => {
    if (!feedbackReviewLink.value) {
        try {
            await createFeedbackRequestQuestion();
            await generateFeedbackLink();
        } catch (error) {
            logUserInteraction("feedback_request_url_copy_error", { error: error.message }, coachingSessionUuid);
            showError(error.message);
            return;
        }
    }
    logUserInteraction("feedback_request_url_copied", { feedback_request_id: feedbackQuestion.value?.hashid }, coachingSessionUuid);

    if (feedbackReviewLink.value) {
        try {
            await navigator.clipboard.writeText(feedbackReviewLink.value);
            showUrlCopied();
        } catch {
            showError("Could not copy to clipboard. Please copy the URL manually: " + feedbackReviewLink.value);
        }
    } else {
        logUserInteraction(
            "feedback_request_url_copy_error",
            { error: "No feedback review link available", feedback_request_id: feedbackQuestion.value?.hashid },
            coachingSessionUuid,
        );
        showError("Error generating feedback review link. Please refresh and try again.");
    }
};
</script>
